/**
 * LOOKING GLASS — Version Store
 * DB NAME:    'looking-glass-db'
 * VERSION:    1
 * STORES:
 *   - versions: keyPath='id', indexes=['canvas_id', 'timestamp']
 */

const DB_NAME    = 'looking-glass-db';
const DB_VERSION = 1;

let db          = null;
let openPromise = null;   // singleton in-flight open promise

function openDB() {
  if (db)          return Promise.resolve(db);
  if (openPromise) return openPromise;

  openPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (e) => {
      const database = e.target.result;
      if (!database.objectStoreNames.contains('versions')) {
        const versionStore = database.createObjectStore('versions', { keyPath: 'id' });
        versionStore.createIndex('canvas_id', 'canvas_id', { unique: false });
        versionStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };

    request.onsuccess = (e) => {
      db          = e.target.result;
      openPromise = null;
      resolve(db);
    };

    request.onerror = (e) => {
      openPromise = null;
      reject(e.target.error);
    };

    request.onblocked = () => {
      console.warn('[LG version store] DB open blocked — another tab may be open with an older version.');
    };
  });

  return openPromise;
}

async function getDB() {
  if (db) return db;
  return openDB();
}

async function tx(storeName, mode = 'readonly') {
  const database = await getDB();
  return database.transaction(storeName, mode).objectStore(storeName);
}

function reqPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror  = () => reject(req.error);
  });
}

export const versionStore = {
  async init() {
    return openDB();
  },

  // ── Versions ────────────────────────────────────────
  async saveVersion(canvasId, snapshot, meta) {
    const versionId = `${canvasId}-${Date.now()}`;
    const version   = {
      id: versionId,
      canvasId,
      snapshot: JSON.parse(JSON.stringify(snapshot)), // ensure plain JSON
      meta: {
        ...meta,
        timestamp: Date.now(),
      },
    };
    const s = await tx('versions', 'readwrite');
    return reqPromise(s.put(version));
  },

  async listVersions(canvasId) {
    const s = await tx('versions');
    const index = s.index('canvas_id');
    return reqPromise(index.getAll(canvasId));
  },

  async getVersion(id) {
    const s = await tx('versions');
    return reqPromise(s.get(id));
  },

  async restoreVersion(id) {
    const version = await this.getVersion(id);
    if (!version) {
      throw new Error(`Version not found: ${id}`);
    }
    // We return the snapshot and meta; the caller must go through HistoryManager
    return {
      snapshot: version.snapshot,
      meta: version.meta,
    };
  },

  async deleteVersion(id) {
    const s = await tx('versions', 'readwrite');
    return reqPromise(s.delete(id));
  },

  async pruneVersions(canvasId, keep) {
    const versions = await this.listVersions(canvasId);
    // Sort by timestamp descending (newest first)
    versions.sort((a, b) => b.meta.timestamp - a.meta.timestamp);
    // Keep the newest `keep` versions, delete the rest
    const toDelete = versions.slice(keep).map(v => v.id);
    if (toDelete.length === 0) return Promise.resolve();
    const s = await tx('versions', 'readwrite');
    // We'll delete in a loop; for simplicity, we do one by one.
    // In a real app, we might use a bulk delete, but IndexedDB doesn't have bulk delete by key array.
    // We'll do a transaction and delete each.
    return Promise.all(toDelete.map(id => reqPromise(s.delete(id))));
  },
};